/**
 * Sourcing workspace schemas — the brand→supplier bridge surface.
 *
 * The brand-enrichment pipeline (Phase 1) decorated brands with KPI
 * aggregates; once the founder marks a brand `decision='selected'`, the
 * sourcing workspace becomes the workbench where they fan discovery out
 * over the brand's top ASINs, review candidates, promote suppliers, and
 * (eventually) send outreach.
 */
import { z } from 'zod';
import { BrandSizeBucket } from './brand';
import { SupplierPipelineStatus } from './primitives';

/** Per-status counts shown as pills on the workspace brand card. */
export const PipelineStatusCounts = z.object({
  lead: z.number().int().nonnegative(),
  contacted: z.number().int().nonnegative(),
  replied: z.number().int().nonnegative(),
  negotiating: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  passed: z.number().int().nonnegative(),
});
export type PipelineStatusCounts = z.infer<typeof PipelineStatusCounts>;

/** One row in the workspace list. */
export const SourcingWorkspaceBrand = z.object({
  name: z.string(),
  domain: z.number().int().positive(),
  sizeBucket: BrandSizeBucket.nullable(),
  productCount: z.number().int().nullable(),
  /** Distinct suppliers pinned to ANY ASIN of this brand. */
  supplierCount: z.number().int().nonnegative(),
  /** Per-status breakdown of those suppliers. */
  pipelineStatusCounts: PipelineStatusCounts,
});
export type SourcingWorkspaceBrand = z.infer<typeof SourcingWorkspaceBrand>;

export const SourcingWorkspaceResponse = z.object({
  brands: z.array(SourcingWorkspaceBrand),
});
export type SourcingWorkspaceResponse = z.infer<typeof SourcingWorkspaceResponse>;

/**
 * Response from POST /api/brands/:name/discover-suppliers. Returns the
 * pre-allocated discovery_runs row IDs so the SPA can subscribe to each
 * via the existing GET /api/discovery-runs/:id polling.
 *
 * Empty `runs` is a legitimate response: the brand may have no products
 * with current_bsr in the products table yet (e.g., never appeared in NL
 * search results). The UI should surface a "no rankable products"
 * message in that case rather than treating it as an error.
 */
export const DiscoverSuppliersForBrandResponse = z.object({
  runs: z.array(
    z.object({
      asin: z.string().length(10),
      discoveryRunId: z.string().uuid(),
    })
  ),
});
export type DiscoverSuppliersForBrandResponse = z.infer<typeof DiscoverSuppliersForBrandResponse>;

// Re-export SupplierPipelineStatus so consumers can type pipeline filters
// without importing from domain directly.
export { SupplierPipelineStatus };
